import { Component, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips'
import { commonImports } from '../shared/table-interface';
import { catchError, combineLatest, firstValueFrom, forkJoin, map, of, switchMap } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { UtilityService } from '../shared/utility.service';
import { Appointment, Bundle, Slot } from 'fhir/r4';
import { StateService } from '../shared/state.service';
import { Subscription } from 'rxjs';
import { AuthService } from '../shared/auth/auth.service';
import { backendEndPointToken } from '../app.config';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';

type AppointmentParticipantType = 'patient' | 'practitioner';

type AppointmentCardParticipant = {
  participantType: AppointmentParticipantType;
  participantId: string;
  reference: string;
  display?: string;
};

type AppointmentCard = {
  appointmentId: string;
  patientName: string;
  patientReference?: string;
  practitionerReference?: string;
  practitionerReferences: string[];
  participants: AppointmentCardParticipant[];
  status: string;
  appointmentType: string;
  start?: string;
  end?: string;
  meetingLink?: string;
  patientPhone?: string;
  patientEmail?: string;
  appointment: Appointment;
};

@Component({
  selector: 'app-appointment-ui',
  providers: [provideNativeDateAdapter()],
  imports: [MatCardModule, MatDatepickerModule, MatSelectModule, DatePipe, CommonModule, MatTabsModule,
    MatButtonModule, MatChipsModule, ReactiveFormsModule, ReferenceDisplayDirective, ...commonImports],
  templateUrl: './appointment-ui.component.html',
  styleUrls: ['../doctors-dashboard/doctors-dashboard.component.scss', './appointment-ui.component.scss']
})
export class AppointmentUiComponent implements OnDestroy {
  appointmentData: { morningHours: Record<string, AppointmentCard>; afternoonHours: Record<string, AppointmentCard> } = {
    morningHours: {},
    afternoonHours: {}
  };

  private utility = inject(UtilityService);
  private http = inject(HttpClient);
  private state = inject(StateService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private backendUrl = inject(backendEndPointToken);
  private fetchSub?: Subscription;
  private readonly meetingUrlRegex = /(https?:\/\/[^\s]+)/i;

  morningHours: string[] = ['08:00', '09:00', '10:00', '11:00'];
  afternoonHours: string[] = ['12:00', '01:00', '02:00', '03:00', '04:00', '05:00'];
  todaysDate = new Date();
  selectedDate = new Date();
  topDates: Array<{ date: Date }> = [];
  tableFilter: Map<string, any[]> = new Map([[
    'status', ['proposed', 'pending', 'booked', 'arrived', 'no-show', 'fulfilled'],
  ],
  ['days', ['Today', 'Tomorrow']]
  ]);

  tableFilterFormControlObject: {
    [key: string]: FormGroup;
  } = {}

  ngOnInit() {
    this.refreshTopDates();
    for (const [key, value] of this.tableFilter) {
      this.tableFilterFormControlObject[key] = new FormGroup({});
      for (const filterValue of value) {
        this.tableFilterFormControlObject[key].addControl(filterValue, new FormControl(false));
      }
    }
    this.fetchAppointments();
  }

  fetchAppointments() {
    this.fetchSub?.unsubscribe();

    const actorRef = this.getLoggedInActorReference();
    const dateStr = this.toDateOnlyString(this.selectedDate);

    const orgId = this.auth.user.getValue()?.organizationId;

    if (this.isAdminUser() && orgId) {
      const baseConfig = {
        resourceType: 'Appointment' as const,
        scope: 'orgWide' as const,
        context: new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true),
        tier1FollowNextLinks: true,
        tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
      };

      this.fetchSub = combineLatest([
        this.utility.threeTierQueryAndSyncToState$<Appointment>({
          ...baseConfig,
          urlSearchObject: { _tag: orgId, date: dateStr }
        }),
        this.utility.threeTierQueryAndSyncToState$<Appointment>({
          ...baseConfig,
          urlSearchObject: { _security: orgId, date: dateStr }
        })
      ]).pipe(
        map(([owned, shared]) => this.dedupeAppointments([...(owned || []), ...(shared || [])])),
        switchMap((appointments) => this.resolveAppointmentsWithSlots$(appointments)),
        map((appointments) => appointments.filter(appointment => this.isAppointmentOnSelectedDate(appointment)))
      ).subscribe(appointments => {
        this.processAppointments(this.dedupeAppointments(appointments || []));
      });
      return;
    }

    if (!actorRef) {
      this.processAppointments([]);
      return;
    }

    this.fetchSub = this.utility.threeTierQueryAndSyncToState$<Appointment>({
      resourceType: 'Appointment',
      scope: 'orgWide',
      urlSearchObject: { actor: actorRef, date: dateStr },
      context: new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true),
      querySearchObject: [
        { key: 'participant.actor.reference', value: [actorRef, `${this.backendUrl}/${actorRef}`] }
      ],
      tier1FollowNextLinks: true,
      tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
    }).pipe(
      map((appointments) => this.dedupeAppointments(appointments || [])),
      switchMap((appointments) => this.resolveAppointmentsWithSlots$(appointments)),
      map((appointments) => appointments.filter(appointment => this.isAppointmentOnSelectedDate(appointment)))
    ).subscribe(appointments => {
      this.processAppointments(this.dedupeAppointments(appointments || []));
    });
  }

  getPatientName(appointment: Appointment): string {
    const patientReferenceExtension = this.findAppointmentExtensionValue(appointment, 'appointment-patient-id');
    if (patientReferenceExtension?.display) {
      return patientReferenceExtension.display;
    }

    const patientParticipant = this.getParticipants(appointment).find(participant => participant.participantType === 'patient');
    if (patientParticipant?.display) {
      return patientParticipant.display;
    }

    if (patientParticipant?.participantId) {
      return patientParticipant.participantId;
    }

    return appointment.description || 'Unknown Patient';
  }

  getParticipants(appointment: Appointment): AppointmentCardParticipant[] {
    const participants: AppointmentCardParticipant[] = [];
    const rawParticipants = this.getAppointmentParticipantItems(appointment);

    for (const participant of rawParticipants) {
      const rawParticipant = participant as any;
      const actorReference = participant.actor?.reference || rawParticipant.participantId || '';
      const inferredType = this.inferParticipantType(actorReference, rawParticipant.participantType);
      const reference = this.normalizeParticipantReference(actorReference, inferredType);
      const participantId = this.extractReferenceId(reference) || this.extractReferenceId(actorReference);

      if (!inferredType || !participantId || !reference) {
        continue;
      }

      participants.push({
        participantType: inferredType,
        participantId,
        reference,
        display: participant.actor?.display
      });
    }

    return participants;
  }

  private getAppointmentParticipantItems(appointment: Appointment): any[] {
    const rawParticipant = (appointment as any).participant;
    const rawParticipants = (appointment as any).participants;

    if (Array.isArray(rawParticipant)) {
      return rawParticipant;
    }

    if (Array.isArray(rawParticipants)) {
      return rawParticipants;
    }

    if (rawParticipant && typeof rawParticipant === 'object') {
      return Object.values(rawParticipant);
    }

    if (rawParticipants && typeof rawParticipants === 'object') {
      return Object.values(rawParticipants);
    }

    return [];
  }

  processAppointments(appointments: Appointment[]) {
    const data: { morningHours: Record<string, AppointmentCard>; afternoonHours: Record<string, AppointmentCard> } = {
      morningHours: {},
      afternoonHours: {}
    };

    const nextMorningHours = new Set(['08:00', '09:00', '10:00', '11:00']);
    const nextAfternoonHours = new Set(['12:00', '01:00', '02:00', '03:00', '04:00', '05:00']);

    appointments.forEach(appt => {
      const effectiveStart = this.getEffectiveAppointmentStart(appt);
      const effectiveEnd = this.getEffectiveAppointmentEnd(appt);
      if (!effectiveStart) return;

      const date = new Date(effectiveStart);
      if (Number.isNaN(date.getTime())) return;

      if (this.toDateOnlyString(date) !== this.toDateOnlyString(this.selectedDate)) {
        return;
      }

      let hour = date.getHours();
      const minute = date.getMinutes();

      const isAfternoon = hour >= 12;
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'

      const timeKey = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      const participants = this.getParticipants(appt);
      const patientParticipant = participants.find(participant => participant.participantType === 'patient');
      const practitionerParticipant = participants.find(participant => participant.participantType === 'practitioner');
      const practitionerReferences = Array.from(new Set(
        participants
          .filter(participant => participant.participantType === 'practitioner')
          .map(participant => participant.reference)
          .filter(Boolean)
      ));

      const entry: AppointmentCard = {
        appointmentId: appt.id || '',
        patientName: this.getPatientName(appt),
        patientReference: patientParticipant?.reference,
        practitionerReference: practitionerParticipant?.reference,
        practitionerReferences,
        participants,
        status: appt.status || 'unknown',
        appointmentType: this.getAppointmentTypeLabel(appt),
        start: effectiveStart,
        end: effectiveEnd,
        meetingLink: this.getMeetingLink(appt),
        patientPhone: this.getAppointmentContactValue(appt, 'appointment-phones', 'patient'),
        patientEmail: this.getAppointmentContactValue(appt, 'appointment-emails', 'patient'),
        appointment: appt
      };

      if (!isAfternoon) {
        data.morningHours[timeKey] = entry;
        nextMorningHours.add(timeKey);
      } else {
        data.afternoonHours[timeKey] = entry;
        nextAfternoonHours.add(timeKey);
      }
    });

    this.morningHours = Array.from(nextMorningHours).sort((left, right) => this.compareTimeKeys(left, right));
    this.afternoonHours = Array.from(nextAfternoonHours).sort((left, right) => this.compareTimeKeys(left, right));
    this.appointmentData = data;
  }

  onDatePicked(event: MatDatepickerInputEvent<Date>) {
    if (!event.value) {
      return;
    }

    this.selectedDate = new Date(event.value);
    this.refreshTopDates();
    this.fetchAppointments();
  }

  selectDate(date: Date) {
    this.selectedDate = new Date(date);
    this.refreshTopDates();
    this.fetchAppointments();
  }

  selectQuickDate(option: string) {
    const nextDate = new Date();

    if (option === 'Tomorrow') {
      nextDate.setDate(nextDate.getDate() + 1);
    } else if (option === 'Next Week') {
      nextDate.setDate(nextDate.getDate() + 7);
    } else if (option === 'Next Month') {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }

    this.selectDate(nextDate);
  }

  isQuickDateSelected(option: string): boolean {
    const date = new Date();

    if (option === 'Tomorrow') {
      date.setDate(date.getDate() + 1);
    } else if (option === 'Next Week') {
      date.setDate(date.getDate() + 7);
    } else if (option === 'Next Month') {
      date.setMonth(date.getMonth() + 1);
    }

    return this.toDateOnlyString(date) === this.toDateOnlyString(this.selectedDate);
  }

  isSelectedDate(date: Date): boolean {
    return this.toDateOnlyString(date) === this.toDateOnlyString(this.selectedDate);
  }

  hasPhone(entry: AppointmentCard | undefined): boolean {
    return !!entry?.patientPhone;
  }

  hasMeetingLink(entry: AppointmentCard | undefined): boolean {
    return !!entry?.meetingLink;
  }

  getCallHref(entry: AppointmentCard | undefined): string | null {
    if (!entry?.patientPhone) {
      return null;
    }

    return `tel:${entry.patientPhone}`;
  }

  openMeetingLink(entry: AppointmentCard | undefined) {
    const appointment = entry?.appointment;
    const appointmentId = (entry?.appointmentId || appointment?.id || '').toString().trim();
    if (!appointmentId || !this.hasMeetingLink(entry)) {
      return;
    }

    void this.navigateToZoomMeeting(appointment, appointmentId);
  }

  private async navigateToZoomMeeting(appointment: Appointment | undefined, appointmentId: string): Promise<void> {
    const meetingRole = this.getZoomMeetingRole();
    const patientId = this.extractPatientId(this.getPatientReference(appointment));

    if (patientId && await this.patientExistsInCurrentTenant(patientId)) {
      this.router.navigate(
        ['/app/patients', patientId, 'zoom-meeting'],
        { queryParams: { role: meetingRole, appointmentId } }
      );
      return;
    }

    this.router.navigate(
      ['/app/zoom-meetings', appointmentId],
      { queryParams: { role: meetingRole, patientId: patientId || undefined, patientContextMissing: patientId ? true : undefined } }
    );
  }

  formatStatus(status: string | undefined): string {
    return (status || 'unknown').replace(/-/g, ' ');
  }

  hasMultiplePractitioners(entry: AppointmentCard | undefined): boolean {
    return (entry?.practitionerReferences?.length ?? 0) > 1;
  }

  get selectedMonthLabel(): string {
    return this.selectedDate.toLocaleDateString(undefined, { month: 'long' });
  }

  get selectedYearLabel(): string {
    return this.selectedDate.toLocaleDateString(undefined, { year: 'numeric' });
  }

  ngOnDestroy(): void {
    this.fetchSub?.unsubscribe();
  }

  private refreshTopDates() {
    const baseDate = new Date(this.selectedDate);
    this.topDates = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + index - 3);
      return { date };
    });
  }

  private dedupeAppointments(appointments: Appointment[]): Appointment[] {
    const seen = new Set<string>();

    return appointments.filter(appointment => {
      if (!appointment?.id) {
        return true;
      }

      if (seen.has(appointment.id)) {
        return false;
      }

      seen.add(appointment.id);
      return true;
    });
  }

  private getLoggedInActorReference(): string {
    const user = this.auth.user.getValue();
    if (!user?.role || !user?.['fhirResourceId']) {
      return '';
    }

    const role = user.role.toString().toLowerCase();
    if (['doctor', 'nurse', 'lab', 'pharmacy', 'consultant', 'admin', 'receptionist', 'serviceprovider', 'cashier'].includes(role)) {
      return `Practitioner/${user['fhirResourceId']}`;
    }

    if (role === 'patient') {
      return `Patient/${user['fhirResourceId']}`;
    }

    return '';
  }

  private isAdminUser(): boolean {
    return String(this.auth.user.getValue()?.role ?? '').toLowerCase() === 'admin';
  }

  private getZoomMeetingRole(): 0 | 1 {
    const currentRole = String(this.auth.user.getValue()?.role ?? '').toLowerCase();
    return currentRole === 'patient' ? 0 : 1;
  }

  private getPatientReference(appointment: Appointment | undefined): string | null {
    for (const participant of appointment?.participant ?? []) {
      const reference = participant.actor?.reference;
      if (reference?.includes('Patient/')) {
        return reference;
      }
    }

    return null;
  }

  private inferParticipantType(referenceOrId: string, participantType?: string): AppointmentParticipantType | null {
    const normalizedType = participantType?.toString().toLowerCase();
    if (normalizedType === 'patient' || normalizedType === 'practitioner') {
      return normalizedType;
    }

    const normalizedReference = (referenceOrId || '').toLowerCase();
    if (normalizedReference.includes('patient/')) {
      return 'patient';
    }
    if (normalizedReference.includes('practitioner/')) {
      return 'practitioner';
    }

    return null;
  }

  private normalizeParticipantReference(referenceOrId: string, participantType: AppointmentParticipantType | null): string {
    const raw = (referenceOrId || '').toString().trim();
    if (!raw || !participantType) {
      return '';
    }

    const resourceType = participantType === 'patient' ? 'Patient' : 'Practitioner';
    const resourceMarker = `${resourceType}/`;
    const markerIndex = raw.indexOf(resourceMarker);
    if (markerIndex >= 0) {
      return raw.slice(markerIndex);
    }

    if (raw.includes('/')) {
      const id = this.extractReferenceId(raw);
      return id ? `${resourceType}/${id}` : '';
    }

    return `${resourceType}/${raw}`;
  }

  private extractReferenceId(reference: string): string {
    const cleanReference = (reference || '').toString().split('?')[0].trim();
    const segments = cleanReference.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  }

  private extractPatientId(reference: string | null): string | null {
    if (!reference) {
      return null;
    }

    const cleanReference = reference.split('?')[0];
    if (cleanReference.includes('/Patient/')) {
      return cleanReference.split('/Patient/')[1]?.split('/')[0] || null;
    }

    if (cleanReference.startsWith('Patient/')) {
      return cleanReference.split('/')[1] || null;
    }

    return null;
  }

  private async patientExistsInCurrentTenant(patientId: string): Promise<boolean> {
    try {
      const bundle = await firstValueFrom(
        this.http.get<Bundle>(`${this.backendUrl}/Patient?_id=${encodeURIComponent(patientId)}&_count=1`, {
          context: new HttpContext().set(LoadingUIEnabled, false)
        }).pipe(catchError(() => of(null)))
      );
      return !!bundle?.entry?.some(entry => entry.resource?.id === patientId);
    } catch {
      return false;
    }
  }

  private findAppointmentExtensionValue(appointment: Appointment, urlFragment: string): any {
    return this.findAppointmentExtensions(appointment, urlFragment)[0];
  }

  private findAppointmentExtensions(appointment: Appointment, urlFragment: string): any[] {
    const ext = Array.isArray(appointment.extension) ? appointment.extension : [];
    return ext.filter(item => (item?.url ?? '').toString().toLowerCase().includes(urlFragment.toLowerCase()));
  }

  private getAppointmentContactValue(
    appointment: Appointment,
    urlFragment: 'appointment-phones' | 'appointment-emails',
    participantType: 'patient' | 'practitioner'
  ): string {
    const extensions = this.findAppointmentExtensions(appointment, urlFragment);

    for (const ext of extensions) {
      if (!ext?.extension) {
        continue;
      }

      const type = ext.extension.find((item: any) => item?.url === 'participantType')?.valueString;
      if (type && type !== participantType) {
        continue;
      }

      const value = ext.extension.find((item: any) => item?.url === 'value')?.valueString || '';
      if (value) {
        return value;
      }
    }

    return '';
  }

  private getMeetingLink(appointment: Appointment): string {
    const direct = this.normalizeMeetingUrl((appointment as any)?.zoomMeetingUrl || (appointment as any)?.meetingLink || (appointment as any)?.meetingUrl);
    if (direct) {
      return direct;
    }

    const ext = Array.isArray(appointment.extension) ? appointment.extension : [];
    for (const item of ext) {
      const url = this.normalizeMeetingUrl(item?.valueUrl || item?.valueUri || item?.valueString);
      if (url) {
        return url;
      }

      const extUrl = (item?.url ?? '').toString().toLowerCase();
      if (extUrl.includes('zoom') || extUrl.includes('meeting') || extUrl.includes('tele')) {
        const candidate = this.normalizeMeetingUrl(item?.valueUrl || item?.valueUri || item?.valueString);
        if (candidate) {
          return candidate;
        }
      }
    }

    const textCandidates: string[] = [appointment.comment, appointment.patientInstruction, appointment.description]
      .filter((value): value is string => typeof value === 'string' && !!value.trim());

    for (const text of textCandidates) {
      const match = text.match(this.meetingUrlRegex);
      if (match?.[1]) {
        const url = this.normalizeMeetingUrl(match[1]);
        if (url) {
          return url;
        }
      }
    }

    return '';
  }

  private normalizeMeetingUrl(value: any): string {
    const raw = (value ?? '').toString().trim();
    if (!raw) {
      return '';
    }

    const withProtocol = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;

    try {
      const url = new URL(withProtocol);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return '';
      }

      return url.toString();
    } catch {
      return '';
    }
  }

  private toDateOnlyString(date: Date): string {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString().split('T')[0];
  }

  private resolveAppointmentsWithSlots$(appointments: Appointment[]) {
    const slotCache = new Map<string, Slot>();
    const cachedSlots = this.state.orgWideResources.slots.getValue();

    for (const item of cachedSlots) {
      const slot = item.actualResource;
      if (slot?.id) {
        slotCache.set(slot.id, slot);
      }
    }

    const missingSlotIds = Array.from(new Set(
      appointments
        .filter(appointment => !appointment.start || !appointment.end)
        .map(appointment => this.getFirstSlotId(appointment))
        .filter((id): id is string => !!id && !slotCache.has(id))
    ));

    if (!missingSlotIds.length) {
      return of(this.attachSlotFallbacks(appointments, slotCache));
    }

    return this.fetchSlotsByIds$(missingSlotIds).pipe(
      map((fetchedSlots) => {
        for (const slot of fetchedSlots) {
          if (slot?.id) {
            slotCache.set(slot.id, slot);
          }
        }

        return this.attachSlotFallbacks(appointments, slotCache);
      }),
      catchError(() => of(this.attachSlotFallbacks(appointments, slotCache)))
    );
  }

  private fetchSlotsByIds$(slotIds: string[]) {
    const context = new HttpContext()
      .set(LoadingUIEnabled, false)
      .set(SkipHospitalSegregation, true);
    const chunkSize = 20;
    const requests: Array<import('rxjs').Observable<Slot[]>> = [];

    for (let index = 0; index < slotIds.length; index += chunkSize) {
      const chunk = slotIds.slice(index, index + chunkSize);
      const url = `${this.backendUrl}/Slot?_id=${chunk.map(id => encodeURIComponent(id)).join(',')}&_count=200`;
      requests.push(this.http.get<Bundle<Slot>>(url, { context }).pipe(
        map(bundle => (bundle.entry ?? [])
          .map(entry => entry.resource)
          .filter((resource): resource is Slot => !!resource && resource.resourceType === 'Slot')),
        catchError(() => of([] as Slot[]))
      ));
    }

    return forkJoin(requests).pipe(
      map((chunks) => chunks.flat())
    );
  }

  private attachSlotFallbacks(appointments: Appointment[], slotMap: Map<string, Slot>): Appointment[] {
    return appointments.map((appointment) => {
      if (appointment.start && appointment.end) {
        return appointment;
      }

      const slotId = this.getFirstSlotId(appointment);
      if (!slotId) {
        return appointment;
      }

      const slot = slotMap.get(slotId);
      if (!slot) {
        return appointment;
      }

      return {
        ...appointment,
        start: appointment.start || slot.start,
        end: appointment.end || slot.end
      };
    });
  }

  private getFirstSlotId(appointment: Appointment): string | null {
    const slotReference = appointment.slot?.[0]?.reference;
    if (!slotReference) {
      return null;
    }

    const cleanReference = slotReference.split('?')[0];
    return cleanReference.startsWith('Slot/') ? cleanReference.split('/')[1] || null : cleanReference || null;
  }

  private getEffectiveAppointmentStart(appointment: Appointment): string | undefined {
    return appointment.start || appointment.requestedPeriod?.[0]?.start || undefined;
  }

  private getEffectiveAppointmentEnd(appointment: Appointment): string | undefined {
    return appointment.end || appointment.requestedPeriod?.[0]?.end || undefined;
  }

  private isAppointmentOnSelectedDate(appointment: Appointment): boolean {
    const effectiveStart = this.getEffectiveAppointmentStart(appointment);
    if (!effectiveStart) {
      return false;
    }

    const date = new Date(effectiveStart);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    return this.toDateOnlyString(date) === this.toDateOnlyString(this.selectedDate);
  }

  private compareTimeKeys(left: string, right: string): number {
    return this.timeKeyToMinutes(left) - this.timeKeyToMinutes(right);
  }

  private timeKeyToMinutes(value: string): number {
    const [hoursText, minutesText] = value.split(':');
    const hours = Number(hoursText);
    const minutes = Number(minutesText);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return Number.MAX_SAFE_INTEGER;
    }

    return hours * 60 + minutes;
  }

  private getAppointmentTypeLabel(appointment: Appointment): string {
    return this.getAppointmentServiceCategoryLabel(appointment) ||
      appointment.appointmentType?.coding?.[0]?.display ||
      appointment.appointmentType?.text ||
      appointment.serviceType?.[0]?.coding?.[0]?.display ||
      appointment.serviceType?.[0]?.text ||
      appointment.description ||
      'General';
  }

  private getAppointmentServiceCategoryLabel(appointment: Appointment): string {
    for (const category of appointment.serviceCategory || []) {
      for (const coding of category.coding || []) {
        const code = coding.code?.toLowerCase();
        if (code === 'telemedicine') return coding.display || category.text || 'Telemedicine';
        if (code === 'consultation') return coding.display || category.text || 'Consultation';
      }
      if (category.text) return category.text;
    }
    return '';
  }

}
